import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';
import { TFunction } from 'i18next';
import groupBy from 'lodash/groupBy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentOrPreviousGroup, Member } from '../../../../../../api/groups';
import { Result } from '../../../../../../utils/results';
import { MaterialIcon } from '../../../../components/MaterialIcon';
import { ExportData } from '../../../api/export';
import { ScorableItem } from '../../../api/scorable-items';
import { useFilters } from '../../../context/filter-context/FilterContext';
import { createLinkWithMembersToFilter } from '../../../context/filter-context/hooks/useSelectedMembers';
import { createLinkWithScorableItemToFilter } from '../../../context/filter-context/hooks/useSelectedScorableItems';
import { ResultModule, useResultsDataContext } from '../../../context/ResultsDataContext';
import useResolvedTableOfContent, { ResolvedTableOfContentNode } from '../../../hooks/useResolvedTableOfContent';
import renderStudent, { renderStudentFullName } from '../../../utils/render-student';
import {
  calculateAccumulatedScore,
  createDetailViewAccumulatedRow,
  createDetailViewRow,
  createTableHeaderForDetailView,
} from '../../exercise/hooks/table-data-utils';
import { AccumulatedRow, TableData, TableDataGroup, TableDataRow } from '../../types';
import useResults from './useResults';

function getFlatNodeIdsFromNodeSubtree(node: ResolvedTableOfContentNode, flatNodeIds: string[] = []): string[] {
  flatNodeIds.push(node.id);
  if (!node.nodes || node.nodes.length === 0) return flatNodeIds;

  for (const child of node.nodes) {
    getFlatNodeIdsFromNodeSubtree(child, flatNodeIds);
  }
  return flatNodeIds;
}

function getScorableItemsForNodes(nodeIds: string[], scorableItems: ScorableItem[], isVem?: boolean) {
  if (isVem) {
    return scorableItems.filter(item => item.exerciseCategories?.some(category => nodeIds.includes(category)));
  }
  return scorableItems.filter(item => nodeIds.includes(item.node));
}

function getResultsToAccumulate(
  memberId: string,
  node: ResolvedTableOfContentNode,
  results: Result[],
  scorableItems?: ScorableItem[],
) {
  const flattenedNodeIdsOfSubtree = getFlatNodeIdsFromNodeSubtree(node);

  const resultsInNode = results.filter(
    result => flattenedNodeIdsOfSubtree.includes(result.nodeId) && result.memberId === memberId,
  );

  if (scorableItems?.length) {
    return resultsInNode.filter(result => {
      return scorableItems.some(scorableItem => {
        const [exerciseId, isAssignmentString] = scorableItem.id.split('-');

        return (
          result.exerciseId === exerciseId &&
          !scorableItem.unachievable &&
          (result.isAssignment === (isAssignmentString === 'true') ||
            scorableItem.isFreeExercise === (isAssignmentString === 'false'))
        );
      });
    });
  }

  return resultsInNode;
}

function getResultForScorableItem(scorableItem: ScorableItem, memberId: string, results: Result[]) {
  return results.find(
    result => result.memberId === memberId && `${result.exerciseId}-${result.isAssignment}` === scorableItem.id,
  );
}

interface CreateAccumulatedRowParams {
  node: ResolvedTableOfContentNode;
  parentNodeId?: string;
  level: number;
  selectedModule: ResultModule;
  selectedMembers: Member[];
  allResults: Result[];
  // if not array given, no filter configured, thus use all scorable items
  scorableItems: ScorableItem[];
}

function createAccumulatedRow({
  node,
  parentNodeId,
  level,
  selectedMembers,
  allResults,
  scorableItems,
}: CreateAccumulatedRowParams): AccumulatedRow {
  return {
    id: node.id,
    tableGroupId: level === 0 ? undefined : parentNodeId,
    title: node.title,
    subTitleTop: node.prefix,
    image: node.image,
    level,
    columns: selectedMembers.map(member => {
      const resultsToAccumulate = getResultsToAccumulate(member.id, node, allResults, scorableItems);

      return {
        title: '',
        ...calculateAccumulatedScore(`${node.id}${member.id}`, resultsToAccumulate, scorableItems)!,
      };
    }),
  };
}

interface createRowGroupRowsParams {
  scorableItemsOfNode: ScorableItem[];
  selectedMembers: Member[];
  allResults: Result[];
  t: TFunction;
  level: number;
  language: SupportedUserLanguage;
}

function createRows({
  scorableItemsOfNode,
  selectedMembers,
  allResults,
  t,
  language,
  level,
}: createRowGroupRowsParams): ScorableItemTableDataRow[] {
  if (scorableItemsOfNode.length === 0) return [];

  return scorableItemsOfNode
    .map(scorableItem => {
      const resultsOfAllMembers = selectedMembers
        .map(member => getResultForScorableItem(scorableItem, member.id, allResults))
        .filter(Boolean) as Result[];

      if (resultsOfAllMembers.length === 0) return null;

      const resultOfLastAssignment = resultsOfAllMembers
        .sort((r1, r2) => {
          const date1 = new Date(r1.achievedAt || '');
          const date2 = new Date(r2.achievedAt || '');
          return date2.getTime() - date1.getTime();
        })
        .find(r => r.isAssignment);

      const scorableItemProps =
        scorableItem.isAssignment && resultOfLastAssignment
          ? ({
              type: 'assignment',
              assignmentRanges: scorableItem.assignmentRanges,
            } as const)
          : ({ type: 'free-exercise' } as const);

      return {
        id: `${scorableItem.id}`,
        tableGroupId: scorableItem.node,
        title: scorableItem.name,
        subTitleBot: t(
          `PAGES.RESULTS.PER_STUDENT.TABLE.SCORABLE_ITEM.TYPE.${
            scorableItem.isAssignment ? 'ASSIGNMENT' : 'FREE-EXERCISE'
          }`,
        ),
        level: level + 1,
        href: createLinkWithScorableItemToFilter([scorableItem.id], language as SupportedUserLanguage),
        ...scorableItemProps,
        icon: (
          <MaterialIcon
            materialType={scorableItem.type}
            contentType={scorableItem.contentType}
            className="scorable-item__image"
          />
        ),

        columns: selectedMembers.map<TableDataRow['columns'][number] | undefined>(member => {
          const result = getResultForScorableItem(scorableItem, member.id, allResults);
          if (!result) return undefined;

          return {
            ...result,
            title: `${member.id}${scorableItem.id}${scorableItem.type}`,
            pending: 0,
            unachievable: scorableItem.unachievable,
          };
        }),
      };
    })
    .filter(Boolean) as ScorableItemTableDataRow[];
}

interface CreateTableBodiesParams {
  node: ResolvedTableOfContentNode;
  scorableItems: ScorableItem[];
  members: Member[];
  allResults: Result[];
  t: TFunction;
  module: ResultModule;
  level?: number;
  language: SupportedUserLanguage;
}

function createTableBodies({
  node,
  scorableItems,
  members,
  allResults,
  t,
  module,
  level = 0,
  language,
}: CreateTableBodiesParams): ScorableItemTableDataGroup | null {
  const nodeHierarchy = getFlatNodeIdsFromNodeSubtree(node);
  if (!nodeHierarchy) return null;
  const isVem = module.isVirtualExerciseModule;
  const scorableItemsInNodeSubtree = getScorableItemsForNodes(nodeHierarchy, scorableItems, isVem);

  const scorableItemsWithResults = scorableItemsInNodeSubtree.filter(scorableItem => {
    const hasResults = members.some(member => {
      const result = getResultForScorableItem(scorableItem, member.id, allResults);
      return result !== undefined;
    });

    return hasResults;
  });

  if (scorableItemsWithResults.length === 0) return null;

  const scorableItemsOfNode = scorableItems.filter(item =>
    isVem ? item.exerciseCategories?.some(category => category === node.id) : item.node === node.id,
  );

  let parentNodeId: string | undefined;
  if (nodeHierarchy.length > 1) {
    const nodeIdIndex = nodeHierarchy.indexOf(node.id);
    parentNodeId = nodeIdIndex > 0 ? nodeHierarchy[nodeIdIndex - 1] : undefined;
  }

  return {
    id: node.id,
    accumulatedRow: createAccumulatedRow({
      node,
      parentNodeId,
      level,
      selectedModule: module,
      selectedMembers: members,
      allResults,
      scorableItems: scorableItemsWithResults,
    }),
    rows: createRows({
      scorableItemsOfNode,
      selectedMembers: members,
      allResults,
      t,
      level,
      language,
    }),
    groups: node.nodes
      ? (node.nodes
          .map(childNode =>
            createTableBodies({
              node: childNode,
              scorableItems: scorableItemsWithResults,
              members: members,
              allResults,
              t,
              module: module,
              level: level + 1,
              language,
            }),
          )
          .filter(Boolean) as ScorableItemTableDataGroup[])
      : [],
  };
}

type ScorableItemTableDataRowBase = TableDataRow & {
  type: 'assignment' | 'free-exercise';
};

export type ScorableItemDataRowAssignment = ScorableItemTableDataRowBase & {
  type: 'assignment';
  assignmentRanges: { from: string; to: string }[];
};

export type ScorableItemDataRowFreeExercise = ScorableItemTableDataRowBase & {
  type: 'free-exercise';
};

export type ScorableItemTableDataRow = ScorableItemDataRowAssignment | ScorableItemDataRowFreeExercise;
export type ScorableItemTableDataGroup = Omit<TableDataGroup, 'rows' | 'groups'> & {
  rows: ScorableItemTableDataRow[];
  groups?: ScorableItemTableDataGroup[];
};
export type ScorableItemTableData = Omit<TableData, 'rows'> & {
  rows: ScorableItemTableDataGroup[];
};

interface CreateDetailTableBodiesParams {
  node: ResolvedTableOfContentNode;
  scorableItems: ScorableItem[];
  member: Member;
  results: Result[];
  level?: number;
  t: TFunction;
  module: ResultModule;
  group: CurrentOrPreviousGroup;
  language: SupportedUserLanguage;
  horizontalScroll: boolean;
}

function createDetailTableBodies({
  scorableItems,
  node,
  member,
  results,
  level = 0,
  t,
  module,
  group,
  language,
  horizontalScroll,
}: CreateDetailTableBodiesParams): TableDataGroup | null {
  const nodeHierarchy = getFlatNodeIdsFromNodeSubtree(node);
  if (!nodeHierarchy) return null;

  const scorableItemsInNodeSubtree = getScorableItemsForNodes(
    nodeHierarchy,
    scorableItems,
    module.isVirtualExerciseModule,
  );
  const scoresToAccumulate = getResultsToAccumulate(member.id, node, results);
  if (scorableItemsInNodeSubtree.length === 0 || scoresToAccumulate.length === 0) return null;

  const scorableItemsOfNode = scorableItems.filter(item =>
    module.isVirtualExerciseModule
      ? item.exerciseCategories?.some(category => category === node.id)
      : item.node === node.id,
  );

  const nodeImage = node.id === 'general' ? module.styling?.logo : node.image;

  return {
    id: node.id,
    accumulatedRow: createDetailViewAccumulatedRow({
      id: node.id,
      title: node.title,
      scoresToAccumulate,
      image: nodeImage,
      scorableItems: scorableItemsOfNode,
      level,
      horizontalScroll,
    }),
    rows: scorableItemsOfNode
      .map(scorableItem => {
        const score = getResultForScorableItem(scorableItem, member.id, results);

        if (!score) return null;

        return createDetailViewRow({
          id: scorableItem.id,
          tableGroupId: scorableItem.node,
          title: scorableItem.name,
          href: createLinkWithScorableItemToFilter([scorableItem.id], language as SupportedUserLanguage),
          level: level + 1,
          subTitleBot: t(
            `PAGES.RESULTS.PER_STUDENT.TABLE.SCORABLE_ITEM.TYPE.${
              scorableItem.isAssignment ? 'ASSIGNMENT' : 'FREE-EXERCISE'
            }`,
          ),
          icon: (
            <MaterialIcon
              materialType={scorableItem.type}
              contentType={scorableItem.contentType}
              className="scorable-item__image"
            />
          ),
          scorableItem,
          result: score,
          member,
          group,
        });
      })
      .filter(Boolean) as TableDataRow[],
    groups: node.nodes
      ? (node.nodes
          .map(childNode =>
            createDetailTableBodies({
              node: childNode,
              scorableItems: scorableItems,
              member,
              results: results,
              level: level + 1,
              t,
              module: module,
              group,
              language: language as SupportedUserLanguage,
              horizontalScroll,
            }),
          )
          .filter(Boolean) as TableDataGroup[])
      : [],
  };
}

export function useTableData({
  showDetailView,
  horizontalScroll,
}: {
  showDetailView: boolean;
  horizontalScroll: boolean;
}) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { loading, scorableItemsPerSchoolYearPerModule } = useResultsDataContext();
  const { toc, loading: tocLoading } = useResolvedTableOfContent();
  const { selectedMembers, selectedGroups, selectedSchoolYear, selectedModule, selectedScorableItems } = useFilters();

  const filteredScorableItemsById = useMemo(() => {
    return groupBy(selectedScorableItems, 'id');
  }, [selectedScorableItems]);

  const { results, loading: resultsLoading, fetchData } = useResults();

  const usedMembers = useMemo(
    () => (!selectedGroups.length ? [] : selectedMembers[selectedGroups[0].id] || []),
    [selectedGroups, selectedMembers],
  );

  const scorableItemsOfSchoolYearAndModule = useMemo(
    () => scorableItemsPerSchoolYearPerModule[selectedSchoolYear?.id || '']?.[selectedModule?.id || ''] || [],
    [scorableItemsPerSchoolYearPerModule, selectedModule?.id, selectedSchoolYear?.id],
  );

  const usedScorableItems = useMemo(() => {
    if (!Object.keys(filteredScorableItemsById).length) {
      return scorableItemsOfSchoolYearAndModule;
    }

    return scorableItemsOfSchoolYearAndModule.filter(item => filteredScorableItemsById[item.id]);
  }, [scorableItemsOfSchoolYearAndModule, filteredScorableItemsById]);

  const tableData = useMemo<ScorableItemTableData>(() => {
    if (Object.values(loading).some(Boolean) || resultsLoading || !usedMembers.length || !selectedModule) {
      return {
        header: {
          title: t('PAGES.RESULTS.PER_STUDENT.TABLE.HEADER.TITLE'),
          columns: [],
        },
        rows: [],
      } as ScorableItemTableData;
    }

    const headerTitle = t('PAGES.RESULTS.PER_STUDENT.TABLE.HEADER.TITLE');

    if (showDetailView) {
      return {
        header: createTableHeaderForDetailView(headerTitle, t),
        rows: toc
          .map(node =>
            createDetailTableBodies({
              node,
              scorableItems: usedScorableItems,
              member: usedMembers[0],
              results: results,
              t,
              module: selectedModule,
              group: selectedGroups[0],
              language: language as SupportedUserLanguage,
              horizontalScroll,
            }),
          )
          .filter(Boolean) as TableDataGroup[],
      } as ScorableItemTableData;
    }

    return {
      header: {
        title: headerTitle,
        columns: usedMembers.map(member => {
          return {
            id: member.id,
            title: renderStudent(member),
            href: createLinkWithMembersToFilter(
              [`${selectedGroups[0].id};${member.id}`],
              [selectedGroups[0].id],
              language as SupportedUserLanguage,
            ),
            tooltipChildren: renderStudentFullName(member),
            minWidth: 'xs',
            maxWidth: 'xs',
          };
        }),
      },
      rows: toc
        .map(node =>
          createTableBodies({
            node,
            scorableItems: usedScorableItems,
            members: usedMembers,
            allResults: results,
            t,
            module: selectedModule,
            language: language as SupportedUserLanguage,
          }),
        )
        .filter(Boolean) as ScorableItemTableDataGroup[],
    };
  }, [
    loading,
    resultsLoading,
    usedMembers,
    selectedModule,
    t,
    showDetailView,
    toc,
    usedScorableItems,
    results,
    selectedGroups,
    horizontalScroll,
    language,
  ]);

  const shownResults = useMemo(() => {
    const res = [];

    for (const scorableItem of usedScorableItems) {
      const resultsForScorableItem = results.filter(result => {
        return (
          result.exerciseId === scorableItem.id.split('-')[0] &&
          result.isAssignment === scorableItem.isAssignment &&
          usedMembers.some(member => result.memberId === member.id)
        );
      });

      if (resultsForScorableItem.length > 0) {
        res.push(...resultsForScorableItem);
      }
    }

    return res;
  }, [results, usedMembers, usedScorableItems]);

  const shownResultsCount = shownResults.length;

  const exportData = useMemo(() => {
    const data: ExportData = {};

    for (const result of shownResults) {
      if (!data[result.memberId]) {
        data[result.memberId] = [];
      }

      data[result.memberId].push({
        exerciseId: result.exerciseId,
        exerciseType: result.exerciseType,
        isAssignment: result.isAssignment,
        isZeroScore: result.isZeroScore,
        amountOfItems: result.amountOfItems,
        achievedAt: result.achievedAt,
        isLti: result.isLti,
        needsGrading: result.needsGrading,
        score: result.score,
        session: result.session,
        duration: result.duration,
      });
    }

    return data;
  }, [shownResults]);

  return {
    data: tableData,
    dataIsLoading:
      Object.values(loading).some(Boolean) || tocLoading || resultsLoading || !selectedModule || !selectedSchoolYear,
    hasResults: shownResultsCount > 0,
    usedMembers,
    usedScorableItems,
    results,
    exportData,
    fetchData,
  };
}
